import { useState, useRef, type ChangeEvent } from 'react'
import {
  LogOut,
  RotateCcw,
  Download,
  Upload,
  Save,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Info,
  Settings,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { MODULES as DEFAULT_MODULES } from '@/data/modules'
import type { useAdminAuth } from '@/hooks/useAdminAuth'
import type { useContentOverrides, ModuleOverride } from '@/hooks/useContentOverrides'
import type { Step } from '@/types'
import type { PageView } from '@/types'

interface AdminPageProps {
  auth: ReturnType<typeof useAdminAuth>
  content: ReturnType<typeof useContentOverrides>
  onNavigate: (view: PageView) => void
}

// ─── small reusable text field ───────────────────────────────────
function Field({
  label,
  value,
  onChange,
  multiline = false,
  rows = 3,
  hint,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  multiline?: boolean
  rows?: number
  hint?: string
}) {
  const cls =
    'w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--bg-primary)] text-[var(--text-primary)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent-blue)] focus:border-transparent transition-all resize-y'
  return (
    <div className="space-y-1">
      <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
        {label}
      </label>
      {hint && <p className="text-xs text-[var(--text-muted)]">{hint}</p>}
      {multiline ? (
        <textarea
          className={cls}
          rows={rows}
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      ) : (
        <input
          type="text"
          className={cls}
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      )}
    </div>
  )
}

// ─── list editor (talking points, additional services) ────────────
function ListEditor({
  label,
  items,
  onChange,
  multiline = false,
}: {
  label: string
  items: string[]
  onChange: (items: string[]) => void
  multiline?: boolean
}) {
  function update(idx: number, val: string) {
    const next = [...items]
    next[idx] = val
    onChange(next)
  }
  function remove(idx: number) {
    onChange(items.filter((_, i) => i !== idx))
  }
  function add() {
    onChange([...items, ''])
  }

  return (
    <div className="space-y-2">
      <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
        {label}
      </label>
      {items.map((item, idx) => (
        <div key={idx} className="flex items-start gap-2">
          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[var(--bg-tertiary)] flex items-center justify-center text-[10px] font-bold text-[var(--text-muted)] mt-2">
            {idx + 1}
          </span>
          {multiline ? (
            <textarea
              rows={2}
              className="flex-1 px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--bg-primary)] text-[var(--text-primary)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent-blue)] resize-y"
              value={item}
              onChange={e => update(idx, e.target.value)}
            />
          ) : (
            <input
              type="text"
              className="flex-1 px-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--bg-primary)] text-[var(--text-primary)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent-blue)]"
              value={item}
              onChange={e => update(idx, e.target.value)}
            />
          )}
          <button
            onClick={() => remove(idx)}
            className="flex-shrink-0 mt-2 p-1.5 rounded text-[var(--text-muted)] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button
        onClick={add}
        className="flex items-center gap-1.5 text-xs text-[var(--accent-blue)] hover:text-[var(--accent-blue-dark)] font-medium mt-1"
      >
        <Plus size={13} /> Add item
      </button>
    </div>
  )
}

// ─── step editor ─────────────────────────────────────────────────
function StepEditor({
  steps,
  onChange,
}: {
  steps: Step[]
  onChange: (steps: Step[]) => void
}) {
  const [expanded, setExpanded] = useState<number | null>(0)

  function updateStep(idx: number, patch: Partial<Step>) {
    const next = steps.map((s, i) => (i === idx ? { ...s, ...patch } : s))
    onChange(next)
  }

  function addStep() {
    const next: Step = {
      number: steps.length + 1,
      title: 'New step',
      description: '',
    }
    onChange([...steps, next])
    setExpanded(steps.length)
  }

  function removeStep(idx: number) {
    const next = steps
      .filter((_, i) => i !== idx)
      .map((s, i) => ({ ...s, number: i + 1 }))
    onChange(next)
    setExpanded(null)
  }

  function moveStep(idx: number, dir: -1 | 1) {
    const next = [...steps]
    const target = idx + dir
    if (target < 0 || target >= next.length) return
    ;[next[idx], next[target]] = [next[target], next[idx]]
    onChange(next.map((s, i) => ({ ...s, number: i + 1 })))
  }

  return (
    <div className="space-y-2">
      <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
        Steps
      </label>
      {steps.map((step, idx) => (
        <div
          key={idx}
          className="rounded-xl border border-[var(--border)] bg-[var(--bg-primary)] overflow-hidden"
        >
          {/* Step header */}
          <div className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-secondary)]">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--accent-blue)] text-white text-xs font-bold flex items-center justify-center">
              {step.number}
            </span>
            <button
              className="flex-1 text-left text-sm font-medium text-[var(--text-primary)] truncate"
              onClick={() => setExpanded(expanded === idx ? null : idx)}
            >
              {step.title || '(untitled step)'}
            </button>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => moveStep(idx, -1)}
                disabled={idx === 0}
                className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-30"
              >
                <ChevronUp size={14} />
              </button>
              <button
                onClick={() => moveStep(idx, 1)}
                disabled={idx === steps.length - 1}
                className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-30"
              >
                <ChevronDown size={14} />
              </button>
              <button
                onClick={() => removeStep(idx)}
                className="p-1 text-[var(--text-muted)] hover:text-red-500 transition-colors"
              >
                <Trash2 size={14} />
              </button>
              <button
                onClick={() => setExpanded(expanded === idx ? null : idx)}
                className="p-1 text-[var(--text-muted)]"
              >
                {expanded === idx ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
          </div>

          {/* Step body */}
          {expanded === idx && (
            <div className="p-3 space-y-3 border-t border-[var(--border)]">
              <Field
                label="Title"
                value={step.title}
                onChange={v => updateStep(idx, { title: v })}
              />
              <Field
                label="Description"
                value={step.description}
                onChange={v => updateStep(idx, { description: v })}
                multiline
                rows={3}
              />
              <ListEditor
                label="Tips (partner tips — shown in orange callout)"
                items={step.tips ?? []}
                onChange={v => updateStep(idx, { tips: v })}
              />
              <ListEditor
                label="Substeps (bullet list — 'What to look for')"
                items={step.substeps ?? []}
                onChange={v => updateStep(idx, { substeps: v })}
              />
            </div>
          )}
        </div>
      ))}
      <button
        onClick={addStep}
        className="flex items-center gap-1.5 text-xs text-[var(--accent-blue)] hover:text-[var(--accent-blue-dark)] font-medium"
      >
        <Plus size={13} /> Add step
      </button>
    </div>
  )
}

// ─── per-module editor ───────────────────────────────────────────
function ModuleEditor({
  moduleId,
  content,
}: {
  moduleId: string
  content: ReturnType<typeof useContentOverrides>
}) {
  const defaultMod = DEFAULT_MODULES.find(m => m.id === moduleId)!
  const merged = content.mergedModules.find(m => m.id === moduleId)!
  const isDirty = content.hasOverride(moduleId)

  const [saved, setSaved] = useState(false)

  function patch(update: ModuleOverride) {
    content.updateModule(moduleId, update)
    setSaved(false)
  }

  function handleSave() {
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  function handleReset() {
    if (confirm(`Reset "${defaultMod.title}" to defaults? This cannot be undone.`)) {
      content.resetModule(moduleId)
    }
  }

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isDirty && <Badge variant="orange">Modified</Badge>}
          {!isDirty && <Badge variant="gray">Default content</Badge>}
        </div>
        <div className="flex gap-2">
          {isDirty && (
            <Button variant="ghost" size="sm" onClick={handleReset}>
              <RotateCcw size={13} />
              Reset to default
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={handleSave}>
            <Save size={13} />
            {saved ? 'Saved!' : 'Save changes'}
          </Button>
        </div>
      </div>

      <Field label="Title" value={merged.title} onChange={v => patch({ title: v })} />
      <Field label="Tagline" value={merged.tagline} onChange={v => patch({ tagline: v })} />
      <Field
        label="Overview"
        value={merged.overview}
        onChange={v => patch({ overview: v })}
        multiline
        rows={4}
      />

      <StepEditor
        steps={merged.steps}
        onChange={steps => patch({ steps })}
      />

      <ListEditor
        label="Key talking points"
        items={merged.keyTalkingPoints}
        onChange={v => patch({ keyTalkingPoints: v })}
        multiline
      />

      {(merged.additionalServices !== undefined || defaultMod.additionalServices) && (
        <ListEditor
          label="Additional services"
          items={merged.additionalServices ?? []}
          onChange={v => patch({ additionalServices: v })}
          multiline
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="Pricing note"
          value={merged.pricingNote ?? ''}
          onChange={v => patch({ pricingNote: v || undefined })}
          hint="Leave blank to hide"
        />
        <Field
          label="Cadence"
          value={merged.cadence ?? ''}
          onChange={v => patch({ cadence: v || undefined })}
          hint="Leave blank to hide"
        />
      </div>

      <Field
        label="Hero angle"
        value={merged.heroAngle ?? ''}
        onChange={v => patch({ heroAngle: v || undefined })}
        multiline
        rows={2}
        hint="Shown as a blue callout box. Leave blank to hide."
      />

      <div className="space-y-2">
        <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
          Real-world example
        </label>
        <Field
          label="Scenario"
          value={merged.realWorldExample?.scenario ?? ''}
          onChange={v =>
            patch({
              realWorldExample: v
                ? { scenario: v, outcome: merged.realWorldExample?.outcome ?? '' }
                : undefined,
            })
          }
          hint="Leave both blank to hide"
        />
        <Field
          label="Outcome"
          value={merged.realWorldExample?.outcome ?? ''}
          onChange={v =>
            patch({
              realWorldExample: merged.realWorldExample?.scenario
                ? { scenario: merged.realWorldExample.scenario, outcome: v }
                : undefined,
            })
          }
        />
      </div>
    </div>
  )
}

// ─── main AdminPage ──────────────────────────────────────────────
export function AdminPage({ auth, content, onNavigate }: AdminPageProps) {
  const [activeTab, setActiveTab] = useState<string>(DEFAULT_MODULES[0].id)
  const [showSettings, setShowSettings] = useState(false)
  const importRef = useRef<HTMLInputElement>(null)

  function handleImport(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      const text = ev.target?.result as string
      const ok = content.importJSON(text)
      if (!ok) alert('Invalid JSON file. Import failed.')
      else alert('Content imported successfully.')
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  const modifiedCount = Object.keys(content.overrides.modules).length

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Settings size={18} className="text-[var(--accent-blue)]" />
            <h1 className="text-xl font-bold text-[var(--text-primary)]">Content Admin</h1>
            {modifiedCount > 0 && (
              <Badge variant="orange">{modifiedCount} module{modifiedCount > 1 ? 's' : ''} modified</Badge>
            )}
          </div>
          <p className="text-sm text-[var(--text-secondary)]">
            Edits are saved to your browser's local storage and applied immediately.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={auth.logout}>
          <LogOut size={14} />
          Log out
        </Button>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-3 rounded-xl bg-[var(--accent-blue-light)] border border-[var(--border)]">
        <Info size={16} className="flex-shrink-0 text-[var(--accent-blue)] mt-0.5" />
        <p className="text-sm text-[var(--text-secondary)]">
          Changes are stored locally in this browser. Use <strong>Export</strong> to save a backup JSON file, and <strong>Import</strong> to restore it on another device or after clearing storage.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" size="sm" onClick={content.exportJSON}>
          <Download size={13} />
          Export JSON
        </Button>
        <Button variant="secondary" size="sm" onClick={() => importRef.current?.click()}>
          <Upload size={13} />
          Import JSON
        </Button>
        <input
          ref={importRef}
          type="file"
          accept=".json"
          className="hidden"
          onChange={handleImport}
        />
        {modifiedCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-red-500 hover:text-red-600"
            onClick={() => {
              if (confirm('Reset ALL modules to defaults? This will delete all your edits.')) {
                content.resetAll()
              }
            }}
          >
            <RotateCcw size={13} />
            Reset all
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onNavigate({ page: 'dashboard' })}
        >
          ← View site
        </Button>

        <button
          className="ml-auto text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] underline"
          onClick={() => setShowSettings(s => !s)}
        >
          Change password instructions
        </button>
      </div>

      {/* Change password help */}
      {showSettings && (
        <Card className="text-sm space-y-2 text-[var(--text-secondary)]">
          <p className="font-semibold text-[var(--text-primary)]">How to change the admin password</p>
          <ol className="list-decimal list-inside space-y-1.5">
            <li>Open your browser console (F12 → Console)</li>
            <li>
              Run:
              <pre className="mt-1 p-2 rounded bg-[var(--bg-tertiary)] text-xs overflow-x-auto">
{`const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode('YourNewPassword'))
console.log(Array.from(new Uint8Array(buf)).map(b=>b.toString(16).padStart(2,'0')).join(''))`}
              </pre>
            </li>
            <li>Copy the hex string that appears</li>
            <li>Open <code className="bg-[var(--bg-tertiary)] px-1 rounded">src/config/admin.ts</code> and replace the <code>ADMIN_PASSWORD_HASH</code> value</li>
            <li>Redeploy the app</li>
          </ol>
        </Card>
      )}

      {/* Module tabs */}
      <div>
        <div className="flex gap-1 overflow-x-auto pb-1 mb-4 border-b border-[var(--border)]">
          {DEFAULT_MODULES.map(mod => (
            <button
              key={mod.id}
              onClick={() => setActiveTab(mod.id)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === mod.id
                  ? 'border-[var(--accent-blue)] text-[var(--accent-blue)] bg-[var(--bg-primary)]'
                  : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]'
              }`}
            >
              {mod.number}. {mod.title}
              {content.hasOverride(mod.id) && (
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-orange)] flex-shrink-0" />
              )}
            </button>
          ))}
        </div>

        <ModuleEditor
          key={activeTab}
          moduleId={activeTab}
          content={content}
        />
      </div>
    </div>
  )
}
